import { ICON_NAMES } from '../../../icon/icon.helper';
import { Icon } from '../../../icon/Icon';
import { TooltipPosition } from '../../../tooltips/Tooltip.types';
import { Pressable, View } from 'react-native';
import { useId, useRef } from 'react';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { styles } from './TotalProductsSummary.styles';
import { TotalProductsSummaryProps, TotalProductsSummaryType } from './TotalProductsSummary.types';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { getFormattedPrice } from 'src/helpers/price.helper';
import { TotalProductsSummaryTooltipContent } from './total-products-summary-tooltip-content/TotalProductsSummaryTooltipContent';
import { BaseHoverTooltip } from '@photosi/rn-components/tooltips/base-hover-tooltip/BaseHoverTooltip';
import { TestIDs } from 'src/constants/testIDs.constants';
import { Icon as IconStyle } from 'src/theme-v2/icon';
import { useFocus } from 'src/hooks/useFocus';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { DEFAULT_FOCUS_RING } from 'src/theme-v2/focus';

export const TotalProductsSummary = observer(
  ({
    totalNumberOfProducts,
    originalPrice,
    estimatePrice,
    type = TotalProductsSummaryType.ON_LIGHT,
    focusOrder = 3,
    focusId,
  }: TotalProductsSummaryProps) => {
    const { i18n } = useStore();
    const ref = useRef(null);
    const generatedId = useId();
    const id = focusId ?? generatedId;
    const { focused } = useFocus({ id, ref, order: focusOrder, group: FocusGroups.HEADER, focusable: true });

    const isDiscounted = !!originalPrice && !!estimatePrice && originalPrice !== estimatePrice;

    return (
      <BaseHoverTooltip
        TooltipContent={TotalProductsSummaryTooltipContent}
        tooltipID={TestIDs.TOOLTIP_DISCOUNT}
        position={TooltipPosition.BOTTOM}
        isArrowVisible={true}
        containerStyle={styles.totalProductsSummaryTooltipContainer}
      >
        <Pressable
          ref={ref}
          tabIndex={0}
          accessibilityLabel={i18n.t('header:total')}
          style={[
            type === TotalProductsSummaryType.ON_LIGHT
              ? styles.totalProductsContainer
              : styles.totalProductsContainerOnDark,
            focused && DEFAULT_FOCUS_RING,
          ]}
          accessibilityLiveRegion="polite"
        >
          <View style={type === TotalProductsSummaryType.ON_LIGHT ? styles.totalProducts : styles.totalProductsOnDark}>
            <TextLato style={styles.totalProductsText}>
              {`${totalNumberOfProducts} ${i18n.t('header:product', { count: totalNumberOfProducts })}`}
            </TextLato>
          </View>
          <View style={styles.priceContainer}>
            {isDiscounted && (
              <TextLato style={styles.originalPrice}>
                {getFormattedPrice({ price: originalPrice, withCurrency: true })}
              </TextLato>
            )}
            <TextLato style={[styles.estimatedPrice, isDiscounted && styles.estimatedPriceDiscounted]}>
              {!!estimatePrice ? ` ${getFormattedPrice({ price: estimatePrice, withCurrency: true })}` : ''}
            </TextLato>
          </View>

          <View style={styles.iconInfo}>
            <Icon name={ICON_NAMES.Info} color={IconStyle.IconColorInteractive} />
          </View>
        </Pressable>
      </BaseHoverTooltip>
    );
  },
);
